// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { initializeIcons, MessageBar, MessageBarType, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TopNav from '../components/TopNav';
import { OpenAiView } from '../views/OpenAiView';
import { useAuthStatus } from '../utils/useAuthStatus';

initializeIcons();

type ReservationResponse = {
  reservationNumber: string;
  candidateName: string;
  candidateEmail: string;
  role: string;
  expiresAt: string;
};

const Interview = (): JSX.Element => {
  const location = useLocation();
  const authProvider = useMemo(() => (location.pathname.startsWith('/app/v2') ? 'login' : 'sso'), [location.pathname]);
  const [reservationNumber, setReservationNumber] = useState('');
  const [candidateName, setCandidateName] = useState<string | null>(null);
  const [interviewRef, setInterviewRef] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, isAdmin, userEmail } = useAuthStatus({
    authProvider,
    redirectUri: location.pathname,
    autoStart: false
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservation = params.get('reservation');
    if (reservation) {
      setReservationNumber(reservation);
    }
  }, [location.search]);

  const handleClaim = async (): Promise<void> => {
    setErrorMessage(null);
    if (!reservationNumber.trim()) {
      setErrorMessage('Please enter your reservation number.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/interviews/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservationNumber })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        setErrorMessage(errorBody?.error || 'Unable to verify reservation.');
        return;
      }

      const data = (await response.json()) as ReservationResponse;
      setCandidateName(data.candidateName);
      setInterviewRef(data.reservationNumber);
    } catch (error) {
      setErrorMessage('Unable to verify reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (candidateName && interviewRef) {
    return <OpenAiView candidateName={candidateName} interviewRef={interviewRef} />;
  }

  return (
    <Stack
      tokens={{ childrenGap: 20 }}
      styles={{ root: { minHeight: '100vh', padding: 32, maxWidth: 560, margin: '0 auto' } }}
    >
      <TopNav isAuthenticated={isAuthenticated} isAdmin={isAdmin} />
      {isAuthenticated && <Text variant="small">Signed in as {userEmail || 'user'}</Text>}
      <Text variant="xLarge">Enter your reservation</Text>
      <Text variant="medium">
        Use the reservation number from your email to access the interview. Once verified, you can begin immediately.
      </Text>
      <TextField
        label="Reservation number"
        value={reservationNumber}
        onChange={(_, value) => setReservationNumber((value || '').toUpperCase())}
        required
      />
      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          {errorMessage}
        </MessageBar>
      )}
      <PrimaryButton onClick={handleClaim} disabled={isSubmitting}>
        {isSubmitting ? 'Checking...' : 'Start interview'}
      </PrimaryButton>
    </Stack>
  );
};

export default Interview;
